import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { SvgIcon } from "@mui/material"
import { Tag, Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import moment from "moment"

import { KonanAvatar } from "../../../../components/core/Avatar"
import type { RootState } from "../../../../store/ReduxStore"
import type { WorkflowList } from "../../../../types/generated/api/WorkflowList"

import styles from "../../Workflows.module.scss"

type SidebarButtonProps = {
  workflow: WorkflowList
  isSelected: boolean
  hasWorkflows: boolean
}

/**
 * Renders a selectable sidebar button for a workflow that displays creator,
 * title, version, creation date, and status information
 *
 * @param {WorkflowList} workflow - Workflow information to display
 * @param {boolean} isSelected - Whether this workflow is currently selected
 * @param {boolean} hasWorkflows  - value which helps in status determination
 * @returns A React element representing the sidebar button
 */
export function SidebarButton(props: Readonly<SidebarButtonProps>): ReactElement {
  // Prop destructuring
  const { workflow, isSelected, hasWorkflows } = props

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "workflowSidebar.sidebarButton",
  })

  // Redux hooks
  const userEmail = useSelector((state: RootState) => state.auth.email)

  return (
    <div className={styles.workflowSidebarButton} aria-selected={isSelected ? "true" : "false"}>
      <Tooltip title={workflow?.created_by} placement="right">
        <SvgIcon className={styles.avatar}>
          <KonanAvatar size={24} name={workflow?.created_by ? workflow?.created_by : (userEmail ?? t("user"))} />
        </SvgIcon>
      </Tooltip>

      <div className={styles.sideBarButtonContentWrapper}>
        <div className={styles.sidebarButtonTitle}>
          <div className={styles.truncateText}>
            <Typography variant="label-bold" noWrap variantColor={isSelected ? 1 : 2} title={workflow?.name}>
              {workflow?.name}
            </Typography>
          </div>

          <Typography
            align="right"
            variant="label-bold"
            color={!workflow?.is_deprecated && workflow?.state !== "DISABLED" ? "positive" : "base"}
            variantColor={2}
          >
            v{workflow?.version}
          </Typography>
        </div>

        <div className={styles.sideBarButtonDate}>
          <Typography variant="label-regular" noWrap className={styles.cursorPointer} variantColor={2}>
            <Tooltip
              title={workflow?.created_at && moment(workflow?.created_at).format("DD/MM/YYYY, h:mm A")}
              placement="right"
            >
              {workflow?.created_at && moment(workflow?.created_at)?.fromNow()}
            </Tooltip>
          </Typography>

          {workflow?.uuid !== "new" && (
            <Tag
              color={workflow?.is_deprecated ? "red" : workflow?.state === "DISABLED" ? "blue" : "green"}
              size="xsmall"
            >
              {workflow?.name
                ? `${workflow?.is_deprecated ? t("tags.deprecated") : workflow?.state === "DISABLED" ? t("tags.idle") : t("tags.active")}`
                : hasWorkflows
                  ? t("tags.idle")
                  : t("tags.active")}
            </Tag>
          )}
        </div>
      </div>
    </div>
  )
}
